// ============================================================================
// LLAMADAS — lo que dice la voz del bot (Oscar) por teléfono (T-ENT-GUION-1 · P3).
//
// Guarda en `chatai.config_llamadas` (migr 0088). La voz la lee al EMPEZAR CADA LLAMADA
// (worker `/voz/sistema` → `voz-agente/src/llamada.ts`): el cambio vale en la siguiente
// llamada, sin reiniciar nada. Casilla vacía ⇒ la voz usa lo de hoy (el código), I-237.
//
// ⛔ Escribe SOLO Victorix (RLS igual que `prompts_bot`, decisión abierta de Cristhian);
//    el resto la ve en solo lectura.
// ⚠️ Las SUGERENCIAS son copia de lo que hoy está en el código (worker `FRASE_OFERTA`,
//    `voz-agente/src/puente.ts`, `VELOCIDAD_DEFECTO`). Si allá cambian, aquí se quedan
//    viejas: son solo una ayuda para escribir, nunca lo que usa la voz.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { Check, Phone } from "lucide-react";
import { Aviso, Boton, Campo, SkeletonLista } from "./piezas.tsx";
import { explicarError, guardarConfigLlamadas, leerConfigLlamadas } from "./datos.ts";
import type { ConfigLlamadas, ContextoPanel, LlavePuente } from "./tipos.ts";

export const SUGERENCIAS_LLAMADA = {
  saludo: "Hola, te habla el asistente virtual de nuestro negocio. ¿En qué te puedo ayudar?",
  frase_oferta:
    "Te cuento algo: ese precio hace parte de una oferta especial que está vigente por cuarenta y ocho horas " +
    "o hasta agotar las unidades que tenemos para entrega rápida. Si se agotan, toca esperar a que vuelvan a ingresar, " +
    "y eso puede tardar varias semanas. Si quieres, te esperamos en el local para que lo veas y lo pruebes; " +
    "y si prefieres hacerlo todo desde tu casa, le pido a un asesor que te escriba a tu WhatsApp.",
  frases_puente: {
    buscar: "Déjame consultar en el sistema",
    cotizar: "Déjame verificar esos precios",
    consultar: "Déjame verificar esa información",
    foto: "Ya te la estoy enviando",
    link: "Te comparto el link",
  } as Record<LlavePuente, string>,
  velocidad: 1.2,
};

const PUENTES: ReadonlyArray<{ llave: LlavePuente; etiqueta: string; ayuda: string }> = [
  { llave: "buscar", etiqueta: "Mientras busca un producto", ayuda: "Buscar en el catálogo, ver una ficha o las categorías." },
  { llave: "cotizar", etiqueta: "Mientras cotiza", ayuda: "Mientras calcula un precio o un combo." },
  { llave: "consultar", etiqueta: "Mientras consulta un dato del negocio", ayuda: "Dirección, horario, envíos, garantía…" },
  { llave: "foto", etiqueta: "Mientras envía una foto", ayuda: "La foto sale al WhatsApp de quien llama." },
  { llave: "link", etiqueta: "Mientras envía un link", ayuda: "El link de la página sale al WhatsApp de quien llama." },
];

type Borrador = Omit<ConfigLlamadas, "tenant_id" | "velocidad"> & { velocidad: string };

function aBorrador(c: ConfigLlamadas | null): Borrador {
  return {
    saludo: c?.saludo ?? "", frase_oferta: c?.frase_oferta ?? "", reglas_extra: c?.reglas_extra ?? "",
    frases_puente: { ...(c?.frases_puente ?? {}) },
    velocidad: c?.velocidad == null ? "" : String(c.velocidad),
  };
}

const vacioANull = (t: string | null | undefined) => (t && t.trim() ? t.trim() : null);

/** Velocidad del borrador → número válido, null (vacía) o `false` (inválida). */
export function velocidadValida(t: string): number | null | false {
  if (!t.trim()) return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) && n >= 0.7 && n <= 1.2 ? n : false;
}

export function Llamadas({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol } = ctx;
  const puedeEditar = rol === "victorix";
  const [b, setB] = useState<Borrador | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    try { setB(aBorrador(await leerConfigLlamadas(sb, tenantId))); setError(null); }
    catch (e) { setError(explicarError(e)); }
    finally { setCargando(false); }
  }, [sb, tenantId]);
  useEffect(() => { void recargar(); }, [recargar]);

  function editar(c: Partial<Borrador>) { setB((x) => (x ? { ...x, ...c } : x)); setGuardado(false); }

  function llenarConSugerencias() {
    if (!b) return;
    editar({
      saludo: b.saludo || SUGERENCIAS_LLAMADA.saludo,
      frase_oferta: b.frase_oferta || SUGERENCIAS_LLAMADA.frase_oferta,
      frases_puente: { ...SUGERENCIAS_LLAMADA.frases_puente, ...Object.fromEntries(
        Object.entries(b.frases_puente).filter(([, v]) => v && v.trim())) },
      velocidad: b.velocidad || String(SUGERENCIAS_LLAMADA.velocidad),
    });
  }

  async function guardar() {
    if (!b || guardando) return;
    const v = velocidadValida(b.velocidad);
    if (v === false) { setError("La velocidad va de 0,7 (más lenta) a 1,2 (más rápida)."); return; }
    const fp: Partial<Record<LlavePuente, string>> = {};
    for (const { llave } of PUENTES) { const t = vacioANull(b.frases_puente[llave]); if (t) fp[llave] = t; }
    setGuardando(true); setError(null);
    try {
      const g = await guardarConfigLlamadas(sb, tenantId, {
        saludo: vacioANull(b.saludo), frase_oferta: vacioANull(b.frase_oferta),
        reglas_extra: vacioANull(b.reglas_extra), frases_puente: fp, velocidad: v,
      });
      setB(aBorrador(g)); setGuardado(true);
    } catch (e) { setError(explicarError(e)); }
    finally { setGuardando(false); }
  }

  const area = (etiqueta: string, valor: string, onCambiar: (v: string) => void, sugerencia: string, max: number, campo: string, filas = 3) => (
    <div className="py-2">
      <label className="block text-[14px] font-medium mb-1">
        {etiqueta}<span className="text-tinta-2 font-normal"> (opcional)</span>
      </label>
      <textarea value={valor} disabled={!puedeEditar} maxLength={max} rows={filas} placeholder={sugerencia}
        data-campo={campo} onChange={(e) => onCambiar(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-elevado text-tinta text-sm border border-borde
          focus:outline-none focus:ring-2 focus:ring-marca/40 disabled:opacity-50 disabled:cursor-not-allowed" />
    </div>
  );

  return (
    <div className="h-full flex flex-col min-w-0" data-pantalla="llamadas">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">Llamadas</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">Lo que dice la voz del bot cuando un cliente llama.</p>
      </header>
      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-4">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          <Aviso tono="alerta">
            {puedeEditar
              ? <>La voz toma estos textos al empezar cada llamada. Una casilla vacía usa lo de hoy
                  (el texto gris de ejemplo).</>
              : <>Esta pantalla es para mirar: lo que dice la voz lo configura Victorix por ahora.</>}
          </Aviso>
          {cargando && !b && <SkeletonLista filas={4} />}
          {b && (<>
            <section className="border border-borde rounded-xl bg-superficie p-4" data-seccion="llamada-textos">
              <h2 className="flex items-center gap-2 text-[14px] font-semibold mb-1">
                <span className="text-tinta-2"><Phone size={15} /></span> Qué dice
              </h2>
              {area("Saludo", b.saludo ?? "", (v) => editar({ saludo: v }), SUGERENCIAS_LLAMADA.saludo, 300, "saludo", 2)}
              {area("Frase de la oferta", b.frase_oferta ?? "", (v) => editar({ frase_oferta: v }), SUGERENCIAS_LLAMADA.frase_oferta, 800, "frase_oferta", 4)}
              {area("Reglas extra", b.reglas_extra ?? "", (v) => editar({ reglas_extra: v }),
                "Ej.: nunca menciones a la competencia.", 2000, "reglas_extra", 3)}
            </section>
            <section className="border border-borde rounded-xl bg-superficie p-4" data-seccion="llamada-puentes">
              <h2 className="text-[14px] font-semibold mb-1">Frases mientras trabaja</h2>
              <p className="text-[12.5px] text-tinta-2 leading-snug mb-1">
                Una frase corta que dice mientras busca o calcula, para que el cliente no oiga silencio.
              </p>
              {PUENTES.map((p) => (
                <Campo key={p.llave} testId={`puente_${p.llave}`} etiqueta={p.etiqueta} ayuda={p.ayuda} maxLength={200}
                  placeholder={SUGERENCIAS_LLAMADA.frases_puente[p.llave]} deshabilitado={!puedeEditar}
                  valor={b.frases_puente[p.llave] ?? ""}
                  onCambiar={(v) => editar({ frases_puente: { ...b.frases_puente, [p.llave]: v } })} />
              ))}
            </section>
            <section className="border border-borde rounded-xl bg-superficie p-4" data-seccion="llamada-velocidad">
              <Campo testId="velocidad" etiqueta="Velocidad de la voz" maxLength={4}
                ayuda="De 0,7 (más lenta) a 1,2 (más rápida). Vacía: la de hoy."
                placeholder={String(SUGERENCIAS_LLAMADA.velocidad)} deshabilitado={!puedeEditar}
                invalido={velocidadValida(b.velocidad) === false}
                valor={b.velocidad} onCambiar={(v) => editar({ velocidad: v })} />
            </section>
            {puedeEditar && (
              <div className="flex flex-wrap items-center gap-3 pb-2">
                <Boton tono="marca" onClick={guardar} disabled={guardando}>
                  {guardando ? "Guardando…" : "Guardar cambios"}
                </Boton>
                <Boton tono="neutro" onClick={llenarConSugerencias} disabled={guardando}>
                  Llenar con lo de hoy
                </Boton>
                {guardado && (
                  <span className="entra flex items-center gap-1 text-[13px] text-ok"><Check size={15} /> Guardado</span>
                )}
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}
